import React, { useEffect, useRef, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { AnswerItem, FileAttachment, QuestionValidationTypes, ReduxState } from 'interface';
import { QuestionSLPRef, SLPQuestionType } from '../../questions.index';
import SLPFile, { SLPFileRef, SLPFileType } from 'utilities/slp/file/file.index';
import { createQuestionTitle } from '../questionsTypes.script';
import { extractTitle } from 'scripts/string';

const SLPFileQuestion: React.ForwardRefRenderFunction<
    QuestionSLPRef,
    ConnectedProps<typeof connector> & SLPQuestionType
> = (props: ConnectedProps<typeof connector> & SLPQuestionType, ref) => {
    React.useImperativeHandle(ref, () => ({
        validate: (e?: boolean) => validate(e),
        submit: (e?: boolean) => onSubmit(e),
    }));
    const lang = props.lang.SLP.questionsTypes;
    const question = props.question;
    const inputRef = useRef<SLPFileRef>(null);
    const [title, setTitle] = useState(question.title);
    const index = props.answerList?.findIndex((i) => i.question.id === question.id);
    const isExist = index !== undefined && index > -1;
    let fileType = SLPFileType.file;
    let multiple = false;
    if (question.validations) {
        const valueFileType = question.validations.find((i) => i.title === QuestionValidationTypes.fileType)?.value;
        if (valueFileType) {
            if (valueFileType.toString() === SLPFileType.voice) {
                fileType = SLPFileType.voice;
            } else if (valueFileType.toString() === SLPFileType.image) {
                fileType = SLPFileType.image;
            } else if (valueFileType.toString() === SLPFileType.film) {
                fileType = SLPFileType.film;
            }
        }
        const valueMultiple = question.validations.find(
            (i) => i.title === QuestionValidationTypes.multiFileSelect,
        )?.value;
        if (valueMultiple && Number(valueMultiple) === 1) multiple = true;
    }

    const [hint, setHint] = useState(question.description);

    useEffect(() => {
        if (question.description)
            setHint(
                createQuestionTitle(
                    question.description,
                    props.answerList,
                    props.answer?.answerer_info,
                    props.form.indicators,
                ),
            );
        setTitle(
            createQuestionTitle(question.title, props.answerList, props.answer?.answerer_info, props.form.indicators),
        );
    }, [props.answerList]);

    function validate(e?: boolean) {
        if (question.is_required && !inputRef.current?.getValue()) {
            if (e) inputRef.current?.setError(lang.cameraEmp);
            return false;
        }
        return true;
    }

    function setAnswer() {
        const data = [...props.answerList];
        const value = inputRef.current?.getValue();
        if (value?.length && validate()) {
            const response: AnswerItem = {
                question: props.question,
                id: question.id,
                answer: props.answer?.id,
                answer_files: value,
            };
            if (index !== undefined && index > -1) data[index] = response;
            else data.push(response);
        } else if (index !== undefined && index > -1) data.splice(index, 1);
        props.setAnswerList(data);
    }

    function onSubmit(e?: boolean) {
        const value = inputRef.current?.getValue();
        if (
            ((value && value.length > 0) ||
                (props.answerList?.find((i) => i.id === question.id)?.value !== undefined && validate(e))) &&
            props.answer
        ) {
            props.submit({
                answer_files: value,
                question: question,
                id: new Date().getTime(),
                created_at: new Date().toISOString(),
            });
        }
    }

    let defaultValue: FileAttachment[] | undefined = undefined;
    if (isExist) {
        defaultValue = props.answerList[index].answer_files;
    }

    return (
        <SLPFile
            ws={props.form.workspace.id}
            color={props.form?.answer_color}
            labelColor={props.form?.question_color}
            hint={extractTitle(hint, false, false)}
            ref={inputRef}
            fileType={fileType}
            counter={props.counter}
            label={extractTitle(title, false, false)}
            image={question.image_file}
            required={question.is_required}
            onChange={setAnswer}
            multiple={multiple}
            maxSize={Number(question.validations?.find((i) => i.title === QuestionValidationTypes.fileMaxSize)?.value)}
            dir={props.form.direction}
            defaultValue={defaultValue}
            accessToken={props.answer?.access_token}
        />
    );
};
const mapStateToProps = (state: ReduxState) => ({
    direction: state.direction,
    lang: state.lang,
});

const connector = connect(mapStateToProps, null, null, { forwardRef: true });
export default connector(React.forwardRef(SLPFileQuestion));
